import { logger } from '../utils/logger';
import { rerankConversationResults, type HybridSearchOptions } from '../utils/bm25l-reranker';
import type { UIBlock } from '../types';

/**
 * Conversation Search result interface
 */
export interface ConversationSearchResult {
	uiBlock: UIBlock;
	similarity: number;
	metadata?: {
		userPrompt?: string;
		timestamp?: number;
	};
	/** Hybrid score combining semantic and BM25L lexical matching */
	hybridScore?: number;
	/** BM25L lexical score */
	bm25Score?: number;
}

/**
 * Options for conversation search with reranking
 */
export interface ConversationSearchOptions {
	userPrompt: string;
	collections?: any;
	userId?: string;
	similarityThreshold?: number;
	/** Enable hybrid reranking with BM25L (default: true) */
	enableReranking?: boolean;
	/** Number of candidates to fetch for reranking (default: 5) */
	rerankCandidates?: number;
	/** Hybrid search options for BM25L reranking */
	hybridOptions?: HybridSearchOptions;
}

/**
 * Search for semantically similar previous conversations for a user
 * Calls the registered 'conversation-history.search' collection
 * Returns the best match if similarity > threshold (default: 0.6)
 *
 * @param userPrompt - The current user's question/prompt
 * @param collections - The collection registry containing conversation-history.search
 * @param similarityThreshold - Minimum similarity threshold (0-1, default: 0.6)
 * @returns Matching UIBlock if found with similarity > threshold, null otherwise
 */
const searchConversations = async ({
	userPrompt,
	collections,
	userId,
	similarityThreshold = 0.6
}: {
	userPrompt: string;
	collections?: any;
	userId?: string;
	similarityThreshold?: number;
}): Promise<ConversationSearchResult | null> => {
	try {
		// Check if conversation-history collection is registered
		if (
			!collections ||
			!collections['conversation-history'] ||
			!collections['conversation-history']['search']
		) {
			logger.info('[ConversationSearch] conversation-history.search collection not registered, skipping');
			return null;
		}

		logger.info(`[ConversationSearch] Searching conversations for: "${userPrompt.substring(0, 50)}..."`);
		logger.info(`[ConversationSearch] Using similarity threshold: ${(similarityThreshold! * 100).toFixed(0)}%`);

		// Call the registered conversation-history.search collection
		const result: ConversationSearchResult = await collections['conversation-history']['search']({
			userPrompt,
			userId,
			threshold: similarityThreshold
		});

		if (!result) {
			logger.info('[ConversationSearch] No matching conversations found');
			return null;
		}

		if (!result.uiBlock) {
			logger.error('[ConversationSearch] No UI block in conversation search result');
			return null;
		}

		const similarity = result.similarity || 0;
		logger.info(`[ConversationSearch] Best match similarity: ${(similarity * 100).toFixed(2)}%`);
		// Check if similarity meets threshold
		if (similarity < similarityThreshold) {
			logger.info(
				`[ConversationSearch] Best match has similarity ${(similarity * 100).toFixed(2)}% but below threshold ${(similarityThreshold * 100).toFixed(2)}%`
			);
			return null;
		}

		logger.info(
			`[ConversationSearch] Found matching conversation with similarity ${(similarity * 100).toFixed(2)}%`
		);
		logger.debug(`[ConversationSearch] Matched prompt: "${result.metadata?.userPrompt?.substring(0, 50)}..."`);

		return result;
	} catch (error) {
		const errorMsg = error instanceof Error ? error.message : String(error);
		logger.warn(`[ConversationSearch] Error searching conversations: ${errorMsg}`);
		// Return null on error - don't fail the entire request
		return null;
	}
};

/**
 * Search for semantically similar previous conversations with hybrid reranking
 *
 * This is an enhanced version of searchConversations that uses BM25L reranking
 * to improve search accuracy by combining semantic similarity with lexical matching.
 *
 * Flow:
 * 1. Fetch a large pool of candidates from ChromaDB (default: 50) with no threshold
 * 2. Apply BM25L reranking on all candidates to find the best lexical + semantic match
 * 3. Return the top result only if it meets the similarity threshold
 *
 * This approach ensures we don't miss good lexical matches that might have
 * lower semantic similarity but are actually more relevant to the query.
 *
 * @param options - Search options including userPrompt, collections, userId, and reranking settings
 * @returns Matching UIBlock if found with hybrid score > threshold, null otherwise
 */
const searchConversationsWithReranking = async (options: ConversationSearchOptions): Promise<ConversationSearchResult | null> => {
	const {
		userPrompt,
		collections,
		userId,
		similarityThreshold = 0.6,
		rerankCandidates = 50, // Fetch more candidates for better reranking
		hybridOptions = {
			semanticWeight: 0.7,
			bm25Weight: 0.3
		}
	} = options;

	try {
		// Check if conversation-history collection is registered
		if (
			!collections ||
			!collections['conversation-history']
		) {
			logger.warn('[ConversationSearch] conversation-history collection not registered, skipping');
			return null;
		}

		// Check if searchMultiple is available for reranking
		if (!collections['conversation-history']['searchMultiple']) {
			logger.warn('[ConversationSearch] searchMultiple not available, falling back to standard search');
			return searchConversations({
				userPrompt,
				collections,
				userId,
				similarityThreshold
			});
		}

		// Fetch a large pool of candidates with NO threshold - let reranking decide
		const results: ConversationSearchResult[] = await collections['conversation-history']['searchMultiple']({
			userPrompt,
			userId,
			limit: rerankCandidates,
			threshold: 0 // No threshold - get all candidates for reranking
		});

		if (!results || results.length === 0) {
			logger.info('[ConversationSearch] No conversations found in database');
			return null;
		}

		logger.info(`[ConversationSearch] Retrieved ${results.length} candidates for reranking`);

		// Prepare results for reranking - extract userPrompt from metadata
		const candidatesForReranking = results.map(r => ({
			...r,
			userPrompt: r.metadata?.userPrompt || ''
		}));

		// Rerank using hybrid BM25L + semantic scoring
		const reranked = rerankConversationResults(userPrompt, candidatesForReranking, hybridOptions);

		if (reranked.length === 0) {
			logger.info('[ConversationSearch] No results after reranking');
			return null;
		}

		// Get the best result after reranking
		const best = reranked[0];
		const hybridScore = best.hybridScore;
		const semanticScore = best.similarity || 0;
		const matchedUserPrompt = (best as any).userPrompt || best.metadata?.userPrompt || '';

		logger.info(`[ConversationSearch] Best match after reranking:`);
		logger.info(`  - Hybrid score: ${(hybridScore * 100).toFixed(2)}%`);
		logger.info(`  - Semantic score: ${(semanticScore * 100).toFixed(2)}%`);
		logger.info(`  - BM25L score: ${best.bm25Score.toFixed(4)}`);
		logger.info(`  - Matched prompt: "${matchedUserPrompt}"`);
		logger.info(`  - Query prompt: "${userPrompt}"`);

		// Apply threshold on SEMANTIC score (not hybrid)
		// Hybrid score can be inflated by normalization with few candidates
		// Semantic score is the true measure of meaning similarity
		// BM25 reranking only helps pick the best among candidates that already pass semantic threshold
		if (semanticScore < similarityThreshold) {
			logger.info(
				`[ConversationSearch] Semantic score ${(semanticScore * 100).toFixed(2)}% below threshold ${(similarityThreshold * 100).toFixed(2)}% - rejecting match`
			);
			return null;
		}

		logger.info(
			`[ConversationSearch] ✓ Found match with semantic score ${(semanticScore * 100).toFixed(2)}%`
		);

		return {
			uiBlock: best.uiBlock,
			similarity: semanticScore,
			hybridScore: hybridScore,
			bm25Score: best.bm25Score,
			metadata: best.metadata
		};
	} catch (error) {
		const errorMsg = error instanceof Error ? error.message : String(error);
		logger.warn(`[ConversationSearch] Error in hybrid search: ${errorMsg}`);
		// Return null on error - don't fail the entire request
		return null;
	}
};

/**
 * Find a previously-stored conversation whose userPrompt is byte-for-byte
 * identical to the current prompt. Backed by an indexed DB lookup via the
 * 'conversation-history.exactMatch' collection — no embeddings, no rerank.
 *
 * Use this for the cached-result short-circuit in the agent flow when only
 * exact (100%) matches should replay. Returns null when the collection isn't
 * registered, when no match exists, or on error.
 */
const findExactMatch = async ({
	userPrompt,
	collections,
	userId,
}: {
	userPrompt: string;
	collections?: any;
	userId?: string;
}): Promise<ConversationSearchResult | null> => {
	try {
		if (
			!collections ||
			!collections['conversation-history'] ||
			!collections['conversation-history']['exactMatch']
		) {
			logger.info('[ConversationSearch] conversation-history.exactMatch collection not registered, skipping');
			return null;
		}

		const result: ConversationSearchResult | null = await collections['conversation-history']['exactMatch']({
			userPrompt,
			userId,
		});

		if (!result || !result.uiBlock) {
			logger.info('[ConversationSearch] No exact match found');
			return null;
		}

		logger.info(`[ConversationSearch] ✓ Exact prompt match found for "${userPrompt.substring(0, 50)}..."`);
		return result;
	} catch (error) {
		const errorMsg = error instanceof Error ? error.message : String(error);
		logger.warn(`[ConversationSearch] Error in exact match lookup: ${errorMsg}`);
		return null;
	}
};

const ConversationSearch = {
	searchConversations,
	searchConversationsWithReranking,
	findExactMatch,
};

export default ConversationSearch;
